import { createBrowserHistory } from "history";

// Confirmations registered by route guards, keyed by the message their block() prompt returns.
const asyncRouteConfirmations = new Map();

/**
 * Registers an in-product confirmation for a route guard. block() has to answer synchronously, so a
 * guard that wants a dialog names a handler here and history awaits it via getUserConfirmation.
 *
 * @param {string} message the string the guard's block() prompt returns for this case
 * @param {() => boolean | Promise<boolean>} handler resolves true to let the navigation through
 * @returns {() => void} removes the registration
 */
export function registerAsyncRouteConfirmation(message, handler) {
    asyncRouteConfirmations.set(message, handler);

    return () => {
        // Guarded so a stale cleanup cannot remove a handler registered later under the same message.
        if (asyncRouteConfirmations.get(message) === handler) {
            asyncRouteConfirmations.delete(message);
        }
    };
}

/**
 * Anything without a registered handler keeps the browser's own confirm(). A handler that throws
 * answers "don't navigate" — a guard failing to answer is not consent. Exported for its tests.
 */
export function getUserConfirmation(message, callback) {
    const handler = asyncRouteConfirmations.get(message);
    if (!handler) {
        callback(window.confirm(message));
        return;
    }

    Promise.resolve()
        .then(handler)
        .then(callback, () => callback(false));
}

export const browserHistory = createBrowserHistory({ getUserConfirmation });

/**
 * Tracks visit order, so a guard blocking a POP can tell Back from Forward — the history package
 * does not say where in the stack a location sits. Keys are recorded as the app pushes them; the
 * entry the app loaded on has none of its own, so it is tracked as null.
 */
export function createNavigationTracker(initialKey = null) {
    const visitedKeys = [initialKey ?? null];
    let currentIndex = 0;

    return {
        record(location, action) {
            const key = location?.key ?? null;

            // A blocked transition re-notifies with the location we are already on. Nothing moved.
            if (key === visitedKeys[currentIndex]) {
                return;
            }

            if (action === "PUSH") {
                // Whatever was ahead is gone, exactly as the browser's own forward stack is.
                visitedKeys.splice(currentIndex + 1);
                visitedKeys.push(key);
                currentIndex = visitedKeys.length - 1;
                return;
            }

            if (action === "REPLACE") {
                visitedKeys[currentIndex] = key;
                return;
            }

            const index = visitedKeys.indexOf(key);
            if (index !== -1) {
                currentIndex = index;
            }
        },

        /**
         * Whether a POP to this location is the user going forward again rather than back. Only a
         * location we have been past can be ahead of us, so an unrecognised one reads as back —
         * which is what the entry the app loaded on is.
         */
        isForward(location) {
            const key = location?.key ?? null;
            if (key === null) {
                return false;
            }

            const index = visitedKeys.indexOf(key);
            return index !== -1 && index > currentIndex;
        }
    };
}

const navigationTracker = createNavigationTracker(browserHistory.location.key);
browserHistory.listen((location, action) => navigationTracker.record(location, action));

export function isForwardNavigation(location) {
    return navigationTracker.isForward(location);
}
